import { computeRemainingClampForecastAllocation } from "./clamp-forecast-po-lines.js";
import {
  clampForecastArrivalMonthFromIso,
  resolveClampForecastExpectedArrivalDate,
  type ClampForecastExpectedArrivalPoLink,
} from "./clamp-forecast-resolve-expected-arrival.js";

/** WYJ clamp POs default to SGS; planned forecast arrivals use the same hub. */
export const CLAMP_FORECAST_PLANNED_ARRIVAL_LOCATION = "sgs";

export interface ClampForecastPlannedArrivalCell {
  id: number;
  category_code: string;
  plan_month: string;
  forecast_qty: number;
  expected_arrival_date: string | null;
}

export interface ClampForecastPlannedArrivalPoLink extends ClampForecastExpectedArrivalPoLink {
  sku?: string;
  quantity_ordered?: number;
}

export interface ClampForecastPlannedArrivalCategory {
  is_kit: boolean;
  fg_sku: string | null;
}

export interface ClampForecastPlannedArrivalKitItem {
  component_sku: string;
  quantity_per_kit: number;
}

export interface ApplyClampForecastPlannedArrivalsOptions {
  categoryByCode?: Map<string, ClampForecastPlannedArrivalCategory>;
  kitItemsByCategory?: Map<string, ClampForecastPlannedArrivalKitItem[]>;
}

export interface ClampForecastPlannedArrivalDerivedRow {
  category_code: string;
  plan_month: string;
  location_code: string;
  rolled_qty_pre_order: number;
  starting_qty: number;
  arrivals: number;
  demand: number;
  pipeline_breakdown?: {
    sku: string;
    rolled_qty_pre_order: number;
    starting_qty: number;
    arrivals: number;
    demand: number;
  }[];
}

function normalizeSkuKey(sku: string): string {
  return sku.trim().toUpperCase();
}

function rechainLocationMonths(rows: ClampForecastPlannedArrivalDerivedRow[]): ClampForecastPlannedArrivalDerivedRow[] {
  const sorted = [...rows].sort((left, right) => left.plan_month.localeCompare(right.plan_month));
  let previousRolled: number | null = null;
  const out: ClampForecastPlannedArrivalDerivedRow[] = [];
  for (const row of sorted) {
    const starting = previousRolled ?? row.starting_qty;
    const nextRow: ClampForecastPlannedArrivalDerivedRow = {
      ...row,
      starting_qty: starting,
      rolled_qty_pre_order: starting + row.arrivals - row.demand,
    };
    if (nextRow.pipeline_breakdown != null) {
      nextRow.pipeline_breakdown = nextRow.pipeline_breakdown.map((part) => ({
        ...part,
        rolled_qty_pre_order: part.starting_qty + part.arrivals - part.demand,
      }));
    }
    previousRolled = nextRow.rolled_qty_pre_order;
    out.push(nextRow);
  }
  return out;
}

function resolvePlannedArrivalInjectionQty(
  cell: ClampForecastPlannedArrivalCell,
  poLinks: ClampForecastPlannedArrivalPoLink[],
  category: ClampForecastPlannedArrivalCategory | undefined,
  kitItems: ClampForecastPlannedArrivalKitItem[],
  rawPurchasedSku: string | null,
): number {
  if (cell.forecast_qty <= 0) return 0;
  const linkedLines = poLinks
    .filter((link) => (link.sku?.trim() ?? "") !== "")
    .map((link) => ({
      sku: link.sku!.trim(),
      quantity_ordered: Number(link.quantity_ordered ?? 0),
    }));
  if (linkedLines.length === 0 && category == null && rawPurchasedSku == null) {
    return cell.forecast_qty;
  }
  return computeRemainingClampForecastAllocation({
    forecast_qty: cell.forecast_qty,
    category: category ?? { is_kit: false, fg_sku: rawPurchasedSku },
    kitItems,
    linkedLines,
  });
}

/**
 * Add clamp forecast quantities to derived-demand arrivals on the month of each
 * cell's explicit expected arrival (or linked PO date). Skips cells with no
 * known arrival date, then re-chains rolled totals forward per location.
 *
 * Linked purchase order lines are already counted by runForecast() PO arrivals,
 * so only the forecast quantity not yet covered by linked PO lines is injected.
 */
export function applyClampForecastPlannedArrivals<
  Row extends ClampForecastPlannedArrivalDerivedRow,
>(
  cells: ClampForecastPlannedArrivalCell[],
  derivedDemand: Row[],
  rawSkuByCategory: Map<string, string | null>,
  poLinksByForecastId: Map<number, ClampForecastPlannedArrivalPoLink[]> = new Map(),
  options: ApplyClampForecastPlannedArrivalsOptions = {},
): Row[] {
  if (cells.length === 0 || derivedDemand.length === 0) return derivedDemand;

  const categoryByCode = options.categoryByCode ?? new Map();
  const kitItemsByCategory = options.kitItemsByCategory ?? new Map();

  const injections = new Map<string, number>();
  for (const cell of cells) {
    const poLinks = poLinksByForecastId.get(cell.id) ?? [];
    const arrivalDate = resolveClampForecastExpectedArrivalDate(cell, poLinks);
    if (arrivalDate == null) continue;
    const arrivalMonth = clampForecastArrivalMonthFromIso(arrivalDate);
    if (arrivalMonth == null) continue;
    const injectQty = resolvePlannedArrivalInjectionQty(
      cell,
      poLinks,
      categoryByCode.get(cell.category_code),
      kitItemsByCategory.get(cell.category_code) ?? [],
      rawSkuByCategory.get(cell.category_code) ?? null,
    );
    if (injectQty <= 0) continue;
    const key = `${cell.category_code}::${arrivalMonth}`;
    injections.set(key, (injections.get(key) ?? 0) + injectQty);
  }
  if (injections.size === 0) return derivedDemand;

  const mutated = derivedDemand.map((row) => ({ ...row, pipeline_breakdown: row.pipeline_breakdown?.map((part) => ({ ...part })) }));

  for (const row of mutated) {
    const injectionKey = `${row.category_code}::${row.plan_month}`;
    const qty = injections.get(injectionKey);
    if (qty == null || qty <= 0) continue;
    if (row.location_code !== CLAMP_FORECAST_PLANNED_ARRIVAL_LOCATION) continue;

    const rawSku = rawSkuByCategory.get(row.category_code) ?? null;
    row.arrivals += qty;
    if (row.pipeline_breakdown != null && row.pipeline_breakdown.length > 0 && rawSku != null) {
      const rawKey = normalizeSkuKey(rawSku);
      const rawPart = row.pipeline_breakdown.find((part) => normalizeSkuKey(part.sku) === rawKey);
      if (rawPart != null) {
        rawPart.arrivals += qty;
      }
    }
  }

  const grouped = new Map<string, Row[]>();
  for (const row of mutated) {
    const groupKey = `${row.category_code}::${row.location_code}`;
    const list = grouped.get(groupKey) ?? [];
    list.push(row);
    grouped.set(groupKey, list);
  }

  const rechained: Row[] = [];
  for (const group of grouped.values()) {
    rechained.push(...(rechainLocationMonths(group) as Row[]));
  }
  rechained.sort((left, right) => {
    const monthDelta = left.plan_month.localeCompare(right.plan_month);
    if (monthDelta !== 0) return monthDelta;
    const categoryDelta = left.category_code.localeCompare(right.category_code);
    if (categoryDelta !== 0) return categoryDelta;
    return left.location_code.localeCompare(right.location_code);
  });
  return rechained;
}
